// Portuguese postal-code prefix ranges by distrito/ilha.
// Usado para dar uma pista visual (não bloqueante) quando o utilizador escolhe
// um distrito e insere um código postal que não faz parte dos prefixos
// conhecidos desse distrito. Os intervalos são simplificações razoáveis dos
// prefixos CTT — cobrem a esmagadora maioria dos casos, com sobreposições
// intencionais em zonas de fronteira administrativa.
//
// Estrutura: { CODE: [ { min, max }, ... ] } — todos os valores são inteiros
// de 4 dígitos (equivalente ao prefixo XXXX do formato XXXX-XXX).

const DISTRICT_RANGES = {
  // Continente – Norte
  VCT: [{ min: 4900, max: 4990 }], // Viana do Castelo
  BRG: [
    { min: 4700, max: 4785 }, // Braga, Vila Verde, Barcelos, Amares
    { min: 4800, max: 4830 }, // Guimarães, Vizela, Fafe
    { min: 4835, max: 4890 }, // Póvoa de Lanhoso, Terras de Bouro, Cabeceiras
  ],
  PRT: [
    { min: 4000, max: 4585 }, // Porto, Matosinhos, Gondomar, Maia, Valongo, VN Gaia, Feira (parte)
    { min: 4400, max: 4585 },
  ],
  AVR: [
    { min: 3700, max: 3899 }, // Aveiro, Águeda, Anadia, Estarreja, Ovar, Ílhavo
    { min: 4500, max: 4550 }, // Espinho, Feira, Arouca, Castelo de Paiva (Aveiro dist.)
  ],
  VIS: [{ min: 3500, max: 3699 }], // Viseu, Tondela, Mangualde, Nelas, Vouzela
  BGC: [{ min: 5100, max: 5399 }], // Bragança, Mirandela, Vinhais, Torre de Moncorvo, Miranda
  VRL: [
    { min: 5000, max: 5100 }, // Vila Real, Peso da Régua
    { min: 5400, max: 5499 }, // Chaves, Montalegre, Boticas, Valpaços
  ],

  // Continente – Centro
  GRD: [{ min: 6200, max: 6440 }], // Guarda, Seia, Gouveia, Manteigas, Trancoso, Sabugal
  CBR: [{ min: 3000, max: 3405 }], // Coimbra, Cantanhede, Figueira da Foz, Miranda do Corvo
  CTB: [
    { min: 6000, max: 6260 }, // Castelo Branco, Covilhã, Fundão, Idanha-a-Nova
    { min: 6300, max: 6320 },
  ],
  LRA: [
    { min: 2400, max: 2500 }, // Leiria, Marinha Grande, Nazaré, Alcobaça
    { min: 3100, max: 3260 }, // Pombal, Ansião, Figueiró dos Vinhos
  ],

  // Continente – Sul e Lisboa
  STR: [
    { min: 2000, max: 2100 }, // Santarém, Rio Maior, Almeirim
    { min: 2100, max: 2260 }, // Coruche, Chamusca, Constância, Torres Novas, Tomar
    { min: 2300, max: 2400 }, // Ferreira do Zêzere
  ],
  LSB: [
    { min: 1000, max: 1999 }, // Lisboa cidade
    { min: 2600, max: 2795 }, // Vila Franca de Xira, Loures, Odivelas, Sintra, Amadora, Cascais, Oeiras
  ],
  STB: [
    { min: 2800, max: 2985 }, // Almada, Seixal, Barreiro, Moita, Montijo, Setúbal, Palmela, Sesimbra
    { min: 7500, max: 7595 }, // Alcácer do Sal, Grândola, Sines, Santiago do Cacém
  ],
  PTG: [{ min: 7300, max: 7480 }], // Portalegre, Elvas, Nisa, Ponte de Sor
  EVR: [{ min: 7000, max: 7200 }], // Évora, Reguengos, Estremoz, Montemor-o-Novo
  BJA: [{ min: 7600, max: 7970 }], // Beja, Moura, Serpa, Odemira, Aljustrel, Cuba
  FAR: [{ min: 8000, max: 8970 }], // Faro, Loulé, Portimão, Albufeira, Lagos, Tavira, Olhão

  // Ilhas
  MAD: [{ min: 9000, max: 9400 }], // Região Autónoma da Madeira
  PDL: [{ min: 9500, max: 9680 }], // Ponta Delgada (S. Miguel, Sta. Maria)
  AGH: [{ min: 9700, max: 9850 }], // Angra do Heroísmo (Terceira, Graciosa, S. Jorge)
  HOR: [{ min: 9800, max: 9980 }], // Horta (Faial, Pico, Flores, Corvo)
};

/**
 * Verifica se o prefixo de um código postal é compatível com o distrito
 * escolhido.
 *
 * Devolve um objeto com `status`:
 *  - "unknown" — CP ainda incompleto (menos de 4 dígitos)
 *  - "match"   — o CP pertence ao distrito
 *  - "mismatch" — o CP claramente não pertence ao distrito; em `suggestions`
 *    virão os códigos dos distritos que incluem o prefixo (pode ser vazio se
 *    for um prefixo desconhecido)
 */
export function checkPostalDistrict(districtCode, postalCode) {
  const digits = (postalCode || "").replace(/\D/g, "").slice(0, 4);
  if (digits.length !== 4) return { status: "unknown" };
  const prefix = parseInt(digits, 10);
  const ranges = DISTRICT_RANGES[districtCode];
  if (!ranges) return { status: "unknown" };
  const inRange = ranges.some((r) => prefix >= r.min && prefix <= r.max);
  if (inRange) return { status: "match" };
  const suggestions = [];
  for (const [code, rs] of Object.entries(DISTRICT_RANGES)) {
    if (code === districtCode) continue;
    if (rs.some((r) => prefix >= r.min && prefix <= r.max)) suggestions.push(code);
  }
  return { status: "mismatch", suggestions };
}

export default { checkPostalDistrict };
